// FILE: UserAvatar.logic.ts
// Purpose: Turn a user's id, name and stored avatar file name into the src, initials and colour the avatar renders.
// Exports: `userAvatarSrc`, `userInitials`, `userAvatarFallbackStyle`.

import { nameToHue } from "~/lib/nameColor";
import { initials } from "~/lib/initials";

export { initials as userInitials };

/**
 * URL of a user's avatar, or `null` when they have none.
 *
 * The file name goes into the query string even though the server ignores it.
 * The response is `Cache-Control: immutable`, so without something in the URL
 * that changes with the file, a user who replaces their picture keeps seeing
 * the old one until the cache expires — which, for `max-age=31536000`, means
 * forever. The path stays `/api/users/:id/avatar` rather than embedding the
 * file name so the client never has to know how the server names files.
 *
 * `origin` is passed in rather than resolved here: this module is pure, and
 * the caller already knows whether it is running in a page the server served
 * (empty origin, relative URL) or against a remote one.
 */
export function userAvatarSrc(input: {
  readonly userId: string;
  readonly avatarFileName: string | null | undefined;
  readonly origin?: string;
}): string | null {
  const fileName = input.avatarFileName?.trim();
  if (!fileName) return null;
  const path = `/api/users/${encodeURIComponent(input.userId)}/avatar`;
  const query = `?v=${encodeURIComponent(fileName)}`;
  const origin = input.origin?.trim() ?? "";
  return origin.length > 0 ? `${origin.replace(/\/+$/, "")}${path}${query}` : `${path}${query}`;
}

/**
 * Background and text colour for the initials, stable per user.
 *
 * Seeded on the id and not the name, so renaming an account does not change
 * the colour people have learned to scan for. Lightness is fixed at a value
 * that carries white text in both themes, which is why the same style object
 * is used light and dark instead of a pair of CSS variables.
 */
export function userAvatarFallbackStyle(seed: string): {
  readonly backgroundColor: string;
  readonly color: string;
} {
  const hue = nameToHue(seed);
  return { backgroundColor: `hsl(${hue} 48% 42%)`, color: "hsl(0 0% 100%)" };
}
